import React, { useRef, useState } from 'react';
import { EendraadSymbol } from './symbols';

// Trikker-stijl eendraadschema, opgebouwd van links ONDER naar boven.
// Teller links onder, hoofdrail onderaan, kringen groeien omhoog.

const KRING_W = 116;
const CONS_H = 58;
const DIFF_H = 70;
const BR_W = 84;
const BOTTOM = 90;
const SUB_W = 100;

const BRANCH_TYPES = ['pv_string', 'batterij', 'backup'];
const KIND_SHORT = { pv: 'PV', rem: 'REM', batterij: 'BAT', laadpaal: 'LP', verdeelbord: 'VB' };

// kWh-meter op een lijn: zelfde symbool als de hoofdteller, maar kleiner (klikbaar om te verwijderen)
const KwhOnLine = ({ x, y, onClick }) => (
  <g transform={`translate(${x}, ${y})`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }} data-testid="kwh-on-line">
    <rect x={-13} y={-11} width={26} height={22} fill="#ffffff" stroke="currentColor" strokeWidth={1.2} />
    <line x1={-13} y1={-4.5} x2={13} y2={-4.5} stroke="currentColor" strokeWidth={1} />
    <text x={0} y={7.5} textAnchor="middle" fontSize={7} fontWeight="bold" fill="currentColor" stroke="none">kWh</text>
  </g>
);

// differentieel-blobje op de tip van het schakelaarblad
const DiffBlob = ({ x, y, angle }) => (
  <ellipse cx={x} cy={y} rx={3.2} ry={1.5} transform={`rotate(${angle} ${x} ${y})`} fill="currentColor" />
);

// ATS-keten: kabel + differentieel + automaat richting verdeelbord (boven een ATS-symbool)
const AtsChain = ({ x, topY, co }) => (
  <g data-testid="ats-chain">
    <line x1={x} y1={topY} x2={x} y2={topY - 14} stroke="currentColor" strokeWidth={1.3} />
    <line x1={x} y1={topY - 14} x2={x - 8} y2={topY - 30} stroke="currentColor" strokeWidth={1.3} />
    <DiffBlob x={x - 8} y={topY - 30} angle={63} />
    <line x1={x} y1={topY - 30} x2={x} y2={topY - 44} stroke="currentColor" strokeWidth={1.3} />
    <line x1={x} y1={topY - 44} x2={x - 8} y2={topY - 60} stroke="currentColor" strokeWidth={1.3} />
    <DiffBlob x={x - 8} y={topY - 60} angle={63} />
    <line x1={x} y1={topY - 60} x2={x} y2={topY - 74} stroke="currentColor" strokeWidth={1.3} />
    <VertSpecs x={x + 12} y={topY - 16} lines={[
      `C ${co.ats_amp || 20}A`,
      `Diff ${co.ats_diff_type || 'A'} \u0394${co.ats_diff_ma || 30}mA`,
      co.ats_cable || '3G4',
    ]} fontSize={7} />
    <text x={x} y={topY - 80} textAnchor="middle" fontSize={8} fontWeight="bold" fill="#111">naar verdeelbord</text>
  </g>
);

const specLines = (co) => {
  if (co.type === 'pv_string') return [
    co.brand ? `Merk: ${co.brand}` : null,
    co.model ? `Type: ${co.model}` : null,
    co.wp ? `Pmax: ${co.wp}Wp` : null,
  ].filter(Boolean);
  if (co.type === 'batterij') {
    const base = [co.brand ? `Merk: ${co.brand}` : null, co.model ? `Type: ${co.model}` : null].filter(Boolean);
    const subs = co.subbatteries || [];
    subs.forEach((sb, i) => {
      const parts = [sb.brand, sb.model, sb.kwh ? `${sb.kwh}kWh` : null, sb.sn].filter(Boolean);
      if (parts.length) base.push(`batt${i + 1}: ${parts.join(' ')}`);
    });
    if (co.sn) base.push(`SN: ${co.sn}`);
    return base;
  }
  if (co.type === 'laadpaal') return [
    co.brand ? `Merk: ${co.brand}` : null,
    co.model ? `Type: ${co.model}` : null,
    co.sn ? `S/N: ${co.sn}` : null,
  ].filter(Boolean);
  if (co.type === 'omvormer' || co.type === 'backup') return [
    co.brand ? `Merk: ${co.brand}` : null,
    co.model ? `Type: ${co.model}` : null,
    co.sn ? `S/N: ${co.sn}` : null,
  ].filter(Boolean);
  return co.specs ? [co.specs] : [];
};

// geroteerde tekst in een kadertje (bv. kortsluitvastheid 3000)
const VertBoxedText = ({ x, y, text, fontSize = 7 }) => {
  const len = String(text).length * fontSize * 0.62 + 5;
  return (
    <g>
      <rect x={x - fontSize - 1.5} y={y - len + 2.5} width={fontSize + 4} height={len} fill="none" stroke="#333" strokeWidth={0.8} />
      <text transform={`translate(${x}, ${y}) rotate(-90)`} fontSize={fontSize} fill="#333">{text}</text>
    </g>
  );
};

// verticale (geroteerde) tekstregels naast elkaar — leest van onder naar boven
const VertSpecs = ({ x, y, lines, max = 8, fontSize = 7 }) => (
  <g>
    {lines.slice(0, max).map((l, i) => (
      <text key={i} transform={`translate(${x + i * 8}, ${y}) rotate(-90)`} fontSize={fontSize} fill="#333">{String(l).slice(0, 32)}</text>
    ))}
  </g>
);

// bouw rijen: omvormer groepeert volgende pv_string/batterij/backup items als takken (met originele index)
const buildRows = (consumers) => {
  const rows = [];
  let i = 0;
  while (i < (consumers || []).length) {
    const co = consumers[i];
    if (co.type === 'omvormer') {
      const branches = [];
      let j = i + 1;
      while (j < consumers.length && BRANCH_TYPES.includes(consumers[j].type)) { branches.push({ co: consumers[j], idx: j }); j++; }
      rows.push({ kind: 'omvormer', co, idx: i, branches });
      i = j;
    } else if (co.type === 'batterij') {
      // thuisbatterij met PV-strings en/of ATS: als takken op een brede omvormer tekenen
      const branches = [];
      let j = i + 1;
      while (j < consumers.length && ['pv_string', 'backup'].includes(consumers[j].type)) { branches.push({ co: consumers[j], idx: j }); j++; }
      rows.push(branches.length ? { kind: 'battinv', co, idx: i, branches } : { kind: 'single', co, idx: i });
      i = j;
    } else {
      rows.push({ kind: 'single', co, idx: i });
      i++;
    }
  }
  return rows;
};

const rowHeight = (row) => {
  if (row.kind === 'single') {
    if (row.co.type === 'backup') return CONS_H + 100;
    if (row.co.type === 'batterij') return CONS_H + 36;
    return CONS_H;
  }
  if (row.kind === 'battinv') return CONS_H + 196 + (row.branches.some(b => b.co.type === 'backup') ? 92 : 0) + 30;
  if (!row.branches.length) return CONS_H;
  return CONS_H + 196 + (row.branches.some(b => b.co.type === 'backup') ? 92 : 0);
};
const circuitHeight = (c) => {
  if (c.kind === 'rem') return 370;
  return buildRows(c.consumers).reduce((a, r) => a + rowHeight(r), 0) + 80 + (c.kind === 'laadpaal' ? 56 : 0);
};
const circuitWidth = (c) => {
  if (c.kind === 'rem') return Math.max(KRING_W, 70 + ((c.subcircuits || []).length) * SUB_W + 40);
  const rows = buildRows(c.consumers);
  const maxBr = Math.max(0, ...rows.map(r => r.kind === 'omvormer' ? r.branches.length : (r.kind === 'battinv' ? r.branches.length + 1 : 0)));
  const hasInvSpecs = rows.some(r => (r.kind === 'omvormer' && specLines(r.co).length > 0) || r.kind === 'battinv');
  return Math.max(KRING_W, maxBr * BR_W + 44) + (hasInvSpecs ? 130 : 0);
};

// back-up (ATS) direct na een thuisbatterij: naast de batterij tekenen i.p.v. erboven
const isBesideBackup = (rows, i) => rows[i]?.kind === 'single' && rows[i].co.type === 'backup'
  && i > 0 && rows[i - 1].kind === 'single' && rows[i - 1].co.type === 'batterij';

export const EendraadCanvas = React.forwardRef(({ tree, meta, signature, onDeleteConsumer, onHideKwh }, ref) => {
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selSym, setSelSym] = useState(null); // { d, c, i, x, y }
  const panDrag = useRef(null);
  const allDiffs = tree?.differentials || [];
  const rootDiffs = allDiffs.filter(d => {
    if (!d.parent_id) return true;
    if (String(d.parent_id).startsWith('vb:')) return false;
    return !allDiffs.some(x => x.id === d.parent_id);
  });
  const childrenOf = (id) => allDiffs.filter(d => d.parent_id === id);
  // verdeelbord-subborden: diffs met parent_id 'vb:<circuitId>'
  const vbDiffsOf = (cid) => allDiffs.filter(d => String(d.parent_id || '') === `vb:${cid}`);
  const diffBlockHeight = (d, dep = 0) => dep > 6 ? CONS_H : DIFF_H + 40 + Math.max(CONS_H, ...(d.circuits || []).map(c => circuitHeightFull(c, dep)), ...childrenOf(d.id).map(k => diffBlockHeight(k, dep + 1)));
  const vbRowHeight = (cid, dep = 0) => {
    const kids = vbDiffsOf(cid);
    return kids.length ? 60 + Math.max(...kids.map(k => diffBlockHeight(k, dep + 1))) : CONS_H;
  };
  const circuitHeightFull = (c, dep = 0) => {
    let h = circuitHeight(c);
    if (c.kind === 'rem') {
      let extra = 0;
      (c.subcircuits || []).forEach(sc => {
        if (sc.kind !== 'laadpaal' && sc.type === 'verdeelbord') {
          const kids = vbDiffsOf(sc.id);
          if (kids.length) extra = Math.max(extra, 40 + Math.max(...kids.map(k => diffBlockHeight(k, dep + 1))));
        }
      });
      return h + extra;
    }
    const rows = buildRows(c.consumers || []);
    rows.forEach((r, i) => {
      if (r.kind === 'single' && r.co.type === 'verdeelbord') h += vbRowHeight(c.id, dep) - CONS_H;
      if (isBesideBackup(rows, i)) h += 64 - rowHeight(r);
    });
    return h;
  };
  const circuitWidthFull = (c) => {
    let w = circuitWidth(c);
    if (c.kind === 'rem') {
      (c.subcircuits || []).forEach(sc => {
        if (sc.kind !== 'laadpaal' && sc.type === 'verdeelbord') w += vbDiffsOf(sc.id).reduce((a, k) => a + blockWidth(k) + 40, 0);
      });
    } else if ((c.consumers || []).some(co => co.type === 'verdeelbord')) {
      w += vbDiffsOf(c.id).reduce((a, k) => a + blockWidth(k) + 40, 0);
    }
    return w;
  };
  const hasBackup = allDiffs.some(d => (d.circuits || []).some(c => (c.consumers || []).some(co => co.type === 'backup')));

  const blockWidth = (diff) => {
    const cw = (diff.circuits || []).reduce((a, c) => a + circuitWidthFull(c), 0);
    const kids = childrenOf(diff.id);
    const kw = kids.reduce((a, k) => a + blockWidth(k) + 40, 0);
    return Math.max(cw + kw + 64, KRING_W);
  };
  const blockDepth = (diff) => {
    const kids = childrenOf(diff.id);
    return 1 + (kids.length ? Math.max(...kids.map(blockDepth)) : 0);
  };
  const maxDepth = rootDiffs.length ? Math.max(...rootDiffs.map(blockDepth)) : 1;
  const maxColH = Math.max(CONS_H, ...allDiffs.flatMap(d => (d.circuits || []).map(c => circuitHeightFull(c))));

  let x = 150;
  const rootBlocks = rootDiffs.map(d => {
    const w = blockWidth(d);
    const b = { diff: d, x, width: w };
    x += w + 46;
    return b;
  });
  const totalW = Math.max(x + 60, 720);
  const hasSig = !!(signature && signature.image);
  const sigH = hasSig ? 114 : 0;
  const totalH = BOTTOM + maxDepth * (DIFF_H + 20) + 160 + maxColH + 60 + 54 + sigH;
  const baseY = totalH - BOTTOM - 54 - sigH;

  const selectSym = (e, d, c, i, sx, sy) => {
    if (!onDeleteConsumer) return;
    e.stopPropagation();
    setSelSym(prev => (prev && prev.d === d && prev.c === c && prev.i === i) ? null : { d, c, i, x: sx, y: sy });
  };

  const renderRow = (row, colX, startY, dId, cId, beside = false, vbDepth = 0) => {
    const co = row.co;
    if (row.kind === 'single') {
      if (co.type === 'kabel') {
        return (
          <g>
            <line x1={colX} y1={startY} x2={colX} y2={startY - (CONS_H - 10)} stroke="currentColor" strokeWidth={1.3} />
            <VertSpecs x={colX + 6} y={startY - 12} lines={[co.specs || '']} />
          </g>
        );
      }
      if (co.type === 'verdeelbord') {
        // verdeelbord = horizontale verzamelrail (T) waar opnieuw differentiëlen + kringen op komen
        const kids = vbDepth > 6 ? [] : vbDiffsOf(cId);
        const railY2 = startY - 26;
        let xc = colX + 26;
        const blocks = kids.map(k => { const b = { k, x: xc }; xc += blockWidth(k) + 40; return b; });
        const railEnd = Math.max(colX + 60, xc - 6);
        return (
          <g data-testid="vb-rail">
            <line x1={colX} y1={startY} x2={colX} y2={railY2} stroke="currentColor" strokeWidth={1.4} />
            <line x1={colX - 12} y1={railY2} x2={railEnd} y2={railY2} stroke="currentColor" strokeWidth={1.8} />
            <text x={colX - 12} y={railY2 - 8} fontSize={7.5} fontWeight="800" fill="#111">VERDEELBORD</text>
            {!kids.length && (
              <text x={colX - 12} y={railY2 + 12} fontSize={6.5} fill="#888">voeg differentieel toe via "Na verdeelbord"</text>
            )}
            {blocks.map(b => <g key={b.k.id}>{renderDiff(b.k, b.x, railY2, false, vbDepth + 1)}</g>)}
          </g>
        );
      }
      if (co.type === 'backup' && beside) {
        // ATS naast de batterij i.p.v. erboven
        const cy = startY - 30;
        const bx = colX + 80;
        return (
          <g>
            <line x1={colX} y1={startY - 4} x2={bx} y2={startY - 4} stroke="currentColor" strokeWidth={1.3} />
            <line x1={bx} y1={startY - 4} x2={bx} y2={cy + 13} stroke="currentColor" strokeWidth={1.3} />
            <g transform={`translate(${bx}, ${cy})`} onClick={(e) => selectSym(e, dId, cId, row.idx, bx, cy)} style={{ cursor: onDeleteConsumer ? 'pointer' : 'inherit' }}>
              <EendraadSymbol type="backup" size={26} />
            </g>
            <AtsChain x={bx} topY={cy - 13} co={co} />
          </g>
        );
      }
      const cy = startY - 30;
      const symType = co.type === 'batterij' ? 'thuisbatterij' : co.type;
      return (
        <g>
          <line x1={colX} y1={startY} x2={colX} y2={cy + 16} stroke="currentColor" strokeWidth={1.3} />
          <g transform={`translate(${colX}, ${cy})`} onClick={(e) => selectSym(e, dId, cId, row.idx, colX, cy)} style={{ cursor: onDeleteConsumer ? 'pointer' : 'inherit' }}>
            <EendraadSymbol type={symType} size={26} />
          </g>
          {co.count > 1 && <text x={colX - 26} y={cy + 4} textAnchor="end" fontSize={10} fontWeight="bold">{`\u00d7${co.count}`}</text>}
          {co.type === 'backup' && <AtsChain x={colX} topY={cy - 13} co={co} />}
          <VertSpecs x={colX + 26} y={co.type === 'batterij' ? cy - 26 : cy + 12} lines={specLines(co)} />
        </g>
      );
    }
    if (row.kind === 'battinv') {
      // thuisbatterij-systeem: brede omvormer onderaan, takken: PV-strings, batterij, ATS
      const cy = startY - 30;
      const pvs = row.branches.filter(b => b.co.type === 'pv_string');
      const atss = row.branches.filter(b => b.co.type === 'backup');
      const cols = [...pvs.map(b => ({ ...b, t: 'pv' })), { t: 'batt' }, ...atss.map(b => ({ ...b, t: 'ats' }))];
      const n = cols.length;
      const iw = Math.max(38, (n - 1) * BR_W + 28);
      const topEdge = cy - 12;
      const invInfo = [co.brand ? `Merk: ${co.brand}` : null, co.model ? `Type: ${co.model}` : null, co.sn ? `S/N: ${co.sn}` : null].filter(Boolean);
      let battLines = (co.subbatteries || []).map((sb, i2) => {
        const parts = [sb.brand, sb.model, sb.kwh ? `${sb.kwh}kWh` : null, sb.sn].filter(Boolean);
        return parts.length ? `batt${i2 + 1}: ${parts.join(' ')}` : null;
      }).filter(Boolean);
      if (!battLines.length) battLines = [co.brand ? `Merk: ${co.brand}` : null, co.model ? `Type: ${co.model}` : null].filter(Boolean);
      let sIdx = 0;
      return (
        <g data-testid="battinv-row">
          <line x1={colX} y1={startY} x2={colX} y2={cy + 12} stroke="currentColor" strokeWidth={1.3} />
          <g transform={`translate(${colX}, ${cy})`} onClick={(e) => selectSym(e, dId, cId, row.idx, colX, cy)} style={{ cursor: onDeleteConsumer ? 'pointer' : 'inherit' }}>
            <EendraadSymbol type="omvormer" size={26} width={iw} />
          </g>
          {invInfo.map((l, i2) => (
            <text key={i2} x={colX + 10} y={cy + 26 + i2 * 9} fontSize={7.5} fill="#333">{String(l).slice(0, 30)}</text>
          ))}
          {cols.map((b, k) => {
            const bx = colX + (k - (n - 1) / 2) * BR_W;
            const by = topEdge - 120;
            if (b.t === 'batt') {
              return (
                <g key={k}>
                  <line x1={bx} y1={topEdge} x2={bx} y2={by + 13} stroke="currentColor" strokeWidth={1.3} />
                  <text x={bx + 3} y={topEdge - 3} fontSize={7} fill="#333">Batt</text>
                  {co.cable && <text transform={`translate(${bx - 4}, ${topEdge - 10}) rotate(-90)`} fontSize={6} fill="#333">{co.cable}</text>}
                  <g transform={`translate(${bx}, ${by})`}>
                    <line x1={-12} y1={-9} x2={12} y2={-9} stroke="currentColor" strokeWidth={2} />
                    <line x1={-6} y1={-3} x2={6} y2={-3} stroke="currentColor" strokeWidth={1.2} />
                    <line x1={-12} y1={3} x2={12} y2={3} stroke="currentColor" strokeWidth={2} />
                    <line x1={-6} y1={9} x2={6} y2={9} stroke="currentColor" strokeWidth={1.2} />
                    <line x1={0} y1={9} x2={0} y2={13} stroke="currentColor" strokeWidth={1.3} />
                  </g>
                  <VertSpecs x={bx + 16} y={by - 14} lines={battLines} fontSize={6.5} />
                </g>
              );
            }
            if (b.t === 'ats') {
              return (
                <g key={k}>
                  <line x1={bx} y1={topEdge} x2={bx} y2={by + 13} stroke="currentColor" strokeWidth={1.3} />
                  <text x={bx + 3} y={topEdge - 3} fontSize={7} fill="#333">ATS</text>
                  <g transform={`translate(${bx}, ${by})`} onClick={(e) => selectSym(e, dId, cId, b.idx, bx, by)} style={{ cursor: onDeleteConsumer ? 'pointer' : 'inherit' }}>
                    <EendraadSymbol type="backup" size={26} />
                  </g>
                  <AtsChain x={bx} topY={by - 13} co={b.co} />
                </g>
              );
            }
            const bc = b.co;
            const bLines = specLines(bc).slice(0, 8);
            return (
              <g key={k}>
                <line x1={bx} y1={topEdge} x2={bx} y2={by + 13} stroke="currentColor" strokeWidth={1.3} />
                <text x={bx + 3} y={topEdge - 3} fontSize={7} fill="#333">{`S${++sIdx}`}</text>
                {bc.cable && <text transform={`translate(${bx - 4}, ${topEdge - 10}) rotate(-90)`} fontSize={6} fill="#333">{bc.cable}</text>}
                <g transform={`translate(${bx}, ${by})`} onClick={(e) => selectSym(e, dId, cId, b.idx, bx, by)} style={{ cursor: onDeleteConsumer ? 'pointer' : 'inherit' }}>
                  <EendraadSymbol type="pv_string" size={26} />
                </g>
                {bc.count > 1 && <text x={bx - 16} y={by - 8} textAnchor="end" fontSize={10} fontWeight="bold">{`\u00d7${bc.count}`}</text>}
                <VertSpecs x={bx + 16} y={by - 18} lines={bLines} fontSize={6.5} />
              </g>
            );
          })}
        </g>
      );
    }
    // omvormer: takken (PV / batterij / back-up) starten rechtstreeks op de omvormer
    const cy = startY - 30;
    const n = row.branches.length;
    const iw = n > 0 ? Math.max(38, (n - 1) * BR_W + 28) : undefined;
    const topEdge = cy - 12;
    const invLines = specLines(co).slice(0, 3);
    return (
      <g>
        <line x1={colX} y1={startY} x2={colX} y2={cy + 12} stroke="currentColor" strokeWidth={1.3} />
        <g transform={`translate(${colX}, ${cy})`} onClick={(e) => selectSym(e, dId, cId, row.idx, colX, cy)} style={{ cursor: onDeleteConsumer ? 'pointer' : 'inherit' }}>
          <EendraadSymbol type="omvormer" size={26} width={iw} />
        </g>
        {/* merk/type omvormer rechts naast de omvormer */}
        {invLines.map((l, i) => (
          <text key={i} x={colX + (iw || 38) / 2 + 6} y={cy + 2 + i * 9} fontSize={7.5} fill="#333">{String(l).slice(0, 26)}</text>
        ))}
        {(() => { let sIdx = 0; return row.branches.map((b, k) => {
          const bc = b.co;
          const bx = colX + (k - (n - 1) / 2) * BR_W;
          const by = topEdge - 120;
          const bLines = specLines(bc).slice(0, 8);
          const connLabel = bc.type === 'pv_string' ? `S${++sIdx}` : (bc.type === 'batterij' ? 'Batt' : null);
          return (
            <g key={k}>
              <line x1={bx} y1={topEdge} x2={bx} y2={by + 13} stroke="currentColor" strokeWidth={1.3} />
              {connLabel && <text x={bx + 3} y={topEdge - 3} fontSize={7} fill="#333">{connLabel}</text>}
              {/* kabelgegevens op de verticale lijn (klein, onder het symbool) */}
              {bc.cable && (
                <text transform={`translate(${bx - 4}, ${topEdge - 10}) rotate(-90)`} fontSize={6} fill="#333">{bc.cable}</text>
              )}
              <g transform={`translate(${bx}, ${by})`} onClick={(e) => selectSym(e, dId, cId, b.idx, bx, by)} style={{ cursor: onDeleteConsumer ? 'pointer' : 'inherit' }}>
                <EendraadSymbol type={bc.type} size={26} />
              </g>
              {bc.count > 1 && <text x={bx - 16} y={by - 8} textAnchor="end" fontSize={10} fontWeight="bold">{`\u00d7${bc.count}`}</text>}
              {/* ATS: kabel + differentieel + automaat richting verdeelbord */}
              {bc.type === 'backup' && <AtsChain x={bx} topY={by - 13} co={bc} />}
              {/* info rechts naast het symbool, verticaal naar boven lezend (batterij: naast de batterijcellen) */}
              <VertSpecs x={bx + 16} y={bc.type === 'batterij' ? by - 26 : by - 18} lines={bLines} fontSize={6.5} />
            </g>
          );
        }); })()}
      </g>
    );
  };

  // kring achter de REM op een horizontale verzamelrail
  const renderRemRail = (c, colX, subY, dId) => {
    const subs = c.subcircuits || [];
    const railY = subY - 100;
    let xCur = colX + 50;
    const pos = subs.map(sc => {
      const sx = xCur;
      xCur += SUB_W;
      if (sc.kind !== 'laadpaal' && sc.type === 'verdeelbord') xCur += vbDiffsOf(sc.id).reduce((a, k) => a + blockWidth(k) + 40, 0);
      return sx;
    });
    const railEnd = pos.length ? pos[pos.length - 1] + 60 : colX + 50;
    return (
      <g data-testid="rem-rail">
        <line x1={colX} y1={subY - 58} x2={colX} y2={railY} stroke="currentColor" strokeWidth={1.4} />
        <line x1={colX - 12} y1={railY} x2={railEnd} y2={railY} stroke="currentColor" strokeWidth={1.8} />
        {subs.map((sc, k) => {
          const sx = pos[k];
          const isVb = sc.kind !== 'laadpaal' && sc.type === 'verdeelbord';
          const sOff = sc.kind === 'laadpaal' ? 56 : 0;
          const bY = railY - sOff;
          const symY = railY - 120 - sOff;
          const scType = sc.kind === 'laadpaal' ? 'laadpaal' : (sc.type || 'stopcontact');
          const short = sc.kind === 'laadpaal' ? 'LP' : ((sc.label || '').toUpperCase().slice(0, 3) || String(k + 1));
          const scLines = sc.kind === 'laadpaal'
            ? [sc.brand ? `Merk: ${sc.brand}` : null, sc.model ? `Type: ${sc.model}` : null, sc.sn ? `S/N: ${sc.sn}` : null].filter(Boolean)
            : (sc.specs ? [sc.specs] : []);
          return (
            <g key={sc.id || k}>
              {/* laadpaal: eerst differentieel op de rail */}
              {sOff > 0 && (
                <g>
                  <line x1={sx} y1={railY} x2={sx} y2={railY - 12} stroke="currentColor" strokeWidth={1.4} />
                  <line x1={sx} y1={railY - 12} x2={sx - 9} y2={railY - 30} stroke="currentColor" strokeWidth={1.4} />
                  <DiffBlob x={sx - 9} y={railY - 30} angle={63} />
                  <line x1={sx} y1={railY - 28} x2={sx} y2={bY} stroke="currentColor" strokeWidth={1.4} />
                  <text transform={`translate(${sx + 10}, ${railY - 2}) rotate(-90)`} fontSize={6} fill="#333">
                    {`Diff ${sc.lp_diff_type || 'A'} \u0394${sc.lp_diff_ma || 30}mA`}
                  </text>
                  <text transform={`translate(${sx + 17}, ${railY - 2}) rotate(-90)`} fontSize={6} fill="#333">
                    {`${sc.lp_diff_amp || 40}A`}
                  </text>
                </g>
              )}
              {/* automaat */}
              <line x1={sx} y1={bY} x2={sx} y2={bY - 14} stroke="currentColor" strokeWidth={1.4} />
              <line x1={sx} y1={bY - 14} x2={sx - 9} y2={bY - 32} stroke="currentColor" strokeWidth={1.4} />
              <DiffBlob x={sx - 9} y={bY - 32} angle={63} />
              <line x1={sx} y1={bY - 30} x2={sx} y2={bY - 42} stroke="currentColor" strokeWidth={1.4} />
              <text transform={`translate(${sx + 14}, ${bY - 6}) rotate(-90)`} fontSize={7} fill="#333">
                {`${sc.poles || 2}P - ${sc.curve || 'C'} ${sc.amperage || 32}A`}
              </text>
              {/* label op de rail (zonder cirkel) */}
              <text x={sx} y={railY + 18} textAnchor="middle" fontSize={9} fontWeight="800" fill="#111">{short}</text>
              {/* kabel verticaal op de lijn */}
              <line x1={sx} y1={bY - 42} x2={sx} y2={isVb ? symY : symY + 14} stroke="currentColor" strokeWidth={1.4} />
              {sc.cable && (
                <text transform={`translate(${sx - 4}, ${bY - 48}) rotate(-90)`} fontSize={6} fill="#333">{sc.cable}</text>
              )}
              {isVb ? (() => {
                const kids = vbDiffsOf(sc.id);
                let xc = sx + 26;
                const blocks = kids.map(kd => { const b = { k: kd, x: xc }; xc += blockWidth(kd) + 40; return b; });
                const vbEnd = Math.max(sx + 60, xc - 6);
                return (
                  <g data-testid="rem-vb-rail">
                    <line x1={sx - 12} y1={symY} x2={vbEnd} y2={symY} stroke="currentColor" strokeWidth={1.8} />
                    <text x={sx - 12} y={symY - 8} fontSize={7.5} fontWeight="800" fill="#111">VERDEELBORD</text>
                    {!kids.length && (
                      <text x={sx - 12} y={symY + 12} fontSize={6.5} fill="#888">voeg differentieel/automaat toe via "Na verdeelbord"</text>
                    )}
                    {blocks.map(b => <g key={b.k.id}>{renderDiff(b.k, b.x, symY, false, 1)}</g>)}
                  </g>
                );
              })() : (
                <>
                  <g transform={`translate(${sx}, ${symY})`}><EendraadSymbol type={scType} size={26} /></g>
                  <VertSpecs x={sx + 16} y={symY - 18} lines={scLines} fontSize={6.5} />
                </>
              )}
            </g>
          );
        })}
      </g>
    );
  };

  const diffsAll = tree?.differentials || [];
  const hasBattery = diffsAll.some(d => (d.circuits || []).some(c => c.kind === 'batterij'));
  const showKwh = hasBattery && tree?.show_kwh !== false;
  const renderDiff = (diff, dx, railY, isRoot = false, vbDepth = 0) => {
    const subY = railY - DIFF_H;
    const circuits = diff.circuits || [];
    const kids = childrenOf(diff.id);
    let colCursor = dx + 64;
    const circuitPos = circuits.map(c => {
      const w = circuitWidthFull(c);
      const hasVb = (c.consumers || []).some(co => co.type === 'verdeelbord');
      const p = { c, colX: colCursor + (hasVb ? 36 : (w > KRING_W ? (c.kind === 'rem' ? 30 : w / 2) : (w - KRING_W) / 2)) };
      colCursor += w;
      return p;
    });
    const kidBlocks = kids.map(k => {
      const b = { diff: k, x: colCursor };
      colCursor += blockWidth(k) + 40;
      return b;
    });
    const subRailEnd = kidBlocks.length
      ? kidBlocks[kidBlocks.length - 1].x + 34
      : (circuitPos.length ? circuitPos[circuitPos.length - 1].colX + 14 : dx + 60);
    const diffInfo = [
      `Type ${diff.diff_type || 'A'} - \u0394${diff.milliamp}mA`,
      `${diff.poles}P - ${diff.amperage}A`,
    ];
    const isAuto = diff.device === 'auto';

    return (
      <g key={diff.id}>
        <g transform={`translate(${dx + 20}, ${railY})`}>
          <line x1={0} y1={0} x2={0} y2={-22} stroke="currentColor" strokeWidth={1.5} />
          <line x1={0} y1={-22} x2={-11} y2={-48} stroke="currentColor" strokeWidth={1.5} />
          <DiffBlob x={-11} y={-48} angle={67} />
          <line x1={0} y1={-46} x2={0} y2={-DIFF_H} stroke="currentColor" strokeWidth={1.5} />
          {isAuto ? (
            <>
              <VertBoxedText x={16} y={-6} text={diff.ka || '3000'} />
              <text transform={`translate(28, -6) rotate(-90)`} fontSize={7} fill="#333">{`${diff.poles}P - ${diff.curve || 'C'} ${diff.amperage}A`}</text>
            </>
          ) : (
            <>
              <text transform={`translate(16, -6) rotate(-90)`} fontSize={7} fill="#333">{diffInfo[0]}</text>
              <VertBoxedText x={28} y={-6} text={diff.ka || '3000'} />
              <text transform={`translate(40, -6) rotate(-90)`} fontSize={7} fill="#333">{diffInfo[1]}</text>
            </>
          )}
          {diff.name && (
            <text transform="translate(-8, -4) rotate(-90)" fontSize={8} fontWeight="800" fill="#111">{diff.name.toUpperCase().slice(0, 14)}</text>
          )}
          {/* kWh grid-tak bij thuisbatterij: kabel + diff-schakelaar + kWh-meter (klik = verwijderen) */}
          {isRoot && showKwh && tree?.show_kwh_main !== false && (() => {
            const y = -DIFF_H + 14;
            const sel = (e) => {
              if (!onHideKwh) return;
              e.stopPropagation();
              setSelSym(prev => (prev && prev.kwhTarget === 'main') ? null : { kwhTarget: 'main', x: dx - 10, y: railY + y });
            };
            return (
              <g onClick={sel} style={{ cursor: onHideKwh ? 'pointer' : 'default' }} data-testid="kwh-grid-tak">
                <line x1={-150} y1={y} x2={-60} y2={y} stroke="currentColor" strokeWidth={1.2} />
                <line x1={-60} y1={y} x2={-44} y2={y - 9} stroke="currentColor" strokeWidth={1.2} />
                <DiffBlob x={-44} y={y - 9} angle={-29} />
                <line x1={-38} y1={y} x2={-30} y2={y} stroke="currentColor" strokeWidth={1.2} />
                <rect x={-30} y={y - 11} width={26} height={22} fill="#ffffff" stroke="currentColor" strokeWidth={1.2} />
                <line x1={-30} y1={y - 4.5} x2={-4} y2={y - 4.5} stroke="currentColor" strokeWidth={1} />
                <text x={-17} y={y + 7} textAnchor="middle" fontSize={6.5} fontWeight="bold" fill="currentColor" stroke="none">kWh</text>
                <line x1={-4} y1={y} x2={0} y2={y} stroke="currentColor" strokeWidth={1.2} />
                <text x={-145} y={y + 12} fontSize={6.5} fill="#333">XGB Cca 4x10</text>
                <text x={-92} y={y + 12} fontSize={6.5} fill="#333">{`Type A - \u0394300mA`}</text>
                <rect x={-93} y={y + 14.5} width={19} height={9} fill="none" stroke="#333" strokeWidth={0.8} />
                <text x={-90.5} y={y + 21.5} fontSize={6.5} fill="#333">3000</text>
                <text x={-92} y={y + 31} fontSize={6.5} fill="#333">4P - 40A</text>
                <text x={-17} y={y + 20} textAnchor="middle" fontSize={6.5} fill="#333">kwh grid</text>
              </g>
            );
          })()}
        </g>
        <line x1={dx + 6} y1={subY} x2={Math.max(subRailEnd, dx + 60)} y2={subY} stroke="currentColor" strokeWidth={1.8} />
        {circuitPos.map(({ c, colX }) => {
          const rows = buildRows(c.consumers);
          const hasLine = !!c.cable || rows.length > 0;
          const showMeter = showKwh && c.kind === 'pv' && c.show_kwh !== false;
          const cableGap = (c.cable && c.kind !== 'rem' && hasLine) ? 34 : 0;
          const meterGap = showMeter ? 34 : 0;
          const infoCols = [
            `${c.poles || 2}P - ${c.curve || 'C'} ${c.amperage}A`,
            c.ka || '3000',
          ];
          const short = c.kind ? KIND_SHORT[c.kind] : null;
          // laadpaal: eerst differentieel, daarna automaat op de lijn
          const dOff = c.kind === 'laadpaal' ? 56 : 0;
          const aY = subY - dOff;
          const compStart2 = aY - 70 - cableGap - meterGap;
          return (
            <g key={c.id}>
              {dOff > 0 && (
                <g data-testid="lp-diff">
                  <line x1={colX} y1={subY} x2={colX} y2={subY - 12} stroke="currentColor" strokeWidth={1.4} />
                  <line x1={colX} y1={subY - 12} x2={colX - 9} y2={subY - 30} stroke="currentColor" strokeWidth={1.4} />
                  <DiffBlob x={colX - 9} y={subY - 30} angle={63} />
                  <line x1={colX} y1={subY - 28} x2={colX} y2={aY} stroke="currentColor" strokeWidth={1.4} />
                  <text transform={`translate(${colX + 10}, ${subY - 2}) rotate(-90)`} fontSize={6} fill="#333">
                    {`Diff ${c.lp_diff_type || 'A'} \u0394${c.lp_diff_ma || 30}mA`}
                  </text>
                  <text transform={`translate(${colX + 17}, ${subY - 2}) rotate(-90)`} fontSize={6} fill="#333">
                    {`${c.lp_diff_amp || 40}A`}
                  </text>
                </g>
              )}
              <line x1={colX} y1={aY} x2={colX} y2={aY - 18} stroke="currentColor" strokeWidth={1.4} />
              <line x1={colX} y1={aY - 18} x2={colX - 10} y2={aY - 40} stroke="currentColor" strokeWidth={1.4} />
              <DiffBlob x={colX - 10} y={aY - 40} angle={66} />
              <line x1={colX} y1={aY - 38} x2={colX} y2={aY - 58} stroke="currentColor" strokeWidth={1.4} />
              {/* info klein + verticaal rechts van de automaat (kortsluitvastheid in kader) */}
              <VertBoxedText x={colX + 17} y={aY - 6} text={c.ka || '3000'} />
              <text transform={`translate(${colX + 29}, ${aY - 6}) rotate(-90)`} fontSize={7} fill="#333">{infoCols[0]}</text>
              {/* kabeltype op de verticale lijn boven de automaat */}
              {c.cable && (hasLine || c.kind === 'rem') && (
                <text transform={`translate(${colX - 5}, ${aY - 62}) rotate(-90)`} fontSize={6.5} fill="#111">{c.cable}</text>
              )}
              {/* kringnaam links van de automaat */}
              {c.label && !short && (
                <text transform={`translate(${colX - 17}, ${subY - 4}) rotate(-90)`} fontSize={8} fontWeight="800" fill="#111">{c.label.toUpperCase().slice(0, 30)}</text>
              )}
              {/* zekeringlabel: korte benaming zonder cirkel (PV/REM/BAT/LP), nummers met cirkel */}
              {short ? (
                <text x={colX} y={subY + 22} textAnchor="middle" fontSize={9} fontWeight="800" fill="#111">{short}</text>
              ) : (
                <>
                  <circle cx={colX} cy={subY + 18} r={9} fill="#ffffff" stroke="#111" strokeWidth={1.5} />
                  <text x={colX} y={subY + 21.5} textAnchor="middle"
                    fontSize={10} fontWeight="bold" fill="#111">{c.circuit_no}</text>
                </>
              )}
              {/* REM: horizontale verzamelrail met kringen */}
              {c.kind === 'rem' && renderRemRail(c, colX, subY, diff.id)}
              {/* doorlopende lijn van automaat naar component (+ kabel en optionele kWh-meter) */}
              {c.kind !== 'rem' && hasLine && <line x1={colX} y1={aY - 58} x2={colX} y2={compStart2} stroke="currentColor" strokeWidth={1.4} />}
              {showMeter && (
                <KwhOnLine x={colX} y={aY - 70 - cableGap - 17} onClick={onHideKwh ? (e) => {
                  e.stopPropagation();
                  setSelSym(prev => (prev && prev.kwhTarget && prev.kwhTarget.c === c.id) ? null : { kwhTarget: { d: diff.id, c: c.id }, x: colX, y: aY - 70 - cableGap - 17 });
                } : undefined} />
              )}
              {c.kind !== 'rem' && (() => {
                let yCursor = compStart2;
                const yStarts = [];
                return rows.map((row, ri) => {
                  const beside = isBesideBackup(rows, ri);
                  const y = beside ? yStarts[ri - 1] : yCursor;
                  yStarts.push(y);
                  const el = <g key={ri}>{renderRow(row, colX, y, diff.id, c.id, beside, vbDepth)}</g>;
                  yCursor -= beside ? 64 : (row.kind === 'single' && row.co.type === 'verdeelbord' ? vbRowHeight(c.id) : rowHeight(row));
                  return el;
                });
              })()}
            </g>
          );
        })}
        {kidBlocks.map(kb => renderDiff(kb.diff, kb.x, subY, false, vbDepth))}
      </g>
    );
  };

  const startPan = (e) => {
    panDrag.current = { sx: e.clientX, sy: e.clientY, ox: pan.x, oy: pan.y };
  };
  const movePan = (e) => {
    if (!panDrag.current) return;
    setPan({ x: panDrag.current.ox + (e.clientX - panDrag.current.sx), y: panDrag.current.oy + (e.clientY - panDrag.current.sy) });
  };
  const endPan = () => { panDrag.current = null; };

  const firstDiffX = rootBlocks.length ? rootBlocks[0].x + 20 : null;
  void hasBackup; void firstDiffX;

  return (
    <svg ref={ref} viewBox={`0 0 ${totalW} ${totalH}`} width="100%" style={{ background: '#ffffff', color: '#1a1a1a', cursor: 'grab' }}
      xmlns="http://www.w3.org/2000/svg" data-testid="eendraad-canvas"
      onMouseDown={startPan} onMouseMove={movePan} onMouseUp={endPan} onMouseLeave={endPan}
      onClick={() => setSelSym(null)}>
      <g id="pan-root" transform={`translate(${pan.x}, ${pan.y})`}>
        {/* Teller links onder */}
        <g transform={`translate(60, ${baseY})`}>
          <rect x={-20} y={-16} width={40} height={32} fill="none" stroke="currentColor" strokeWidth={1.3} />
          <line x1={-20} y1={-7} x2={20} y2={-7} stroke="currentColor" strokeWidth={1.1} />
          <text x={0} y={9} textAnchor="middle" fontSize={9.5} fontWeight="bold">kWh</text>
          <text x={0} y={30} textAnchor="middle" fontSize={9}>{tree?.supply?.label || 'Teller'}</text>
          <text x={0} y={42} textAnchor="middle" fontSize={8.5} fill="#555">{`${tree?.supply?.voltage || '230V'} ~ 50Hz / ${tree?.supply?.amperage || 40}A`}</text>
          <g transform="translate(0, 58)">
            <line x1={0} y1={0} x2={0} y2={10} stroke="currentColor" strokeWidth={1.3} />
            <line x1={-9} y1={10} x2={9} y2={10} stroke="currentColor" strokeWidth={1.3} />
            <line x1={-6} y1={14} x2={6} y2={14} stroke="currentColor" strokeWidth={1.3} />
            <line x1={-3} y1={18} x2={3} y2={18} stroke="currentColor" strokeWidth={1.3} />
          </g>
        </g>
        {/* ATS wordt enkel bij de omvormer getekend, niet meer tussen teller en hoofddifferentieel */}
        {rootBlocks.length > 0 && (
          <>
            <line x1={86} y1={baseY} x2={rootBlocks[rootBlocks.length - 1].x + 34} y2={baseY}
              stroke="currentColor" strokeWidth={2} />
            <text x={(86 + rootBlocks[0].x + 20) / 2} y={baseY + 14} textAnchor="middle" fontSize={9} fill="#333">
              {tree?.supply?.cable || '4G10'}
            </text>
          </>
        )}
        {rootBlocks.length === 0 && (
          <text x={totalW / 2} y={totalH / 2} textAnchor="middle" fontSize={15} fill="#999">
            Voeg een differentieelschakelaar toe om het schema op te bouwen
          </text>
        )}
        {rootBlocks.map(({ diff, x: dx }) => renderDiff(diff, dx, baseY, true))}
        {/* geselecteerd symbool of kWh-meter: verwijderknop */}
        {selSym && (onDeleteConsumer || onHideKwh) && (
          <g>
            <circle cx={selSym.x} cy={selSym.y} r={22} fill="rgba(220,38,38,0.06)" stroke="#dc2626" strokeWidth={1} strokeDasharray="4 3" />
            <g onClick={(e) => {
              e.stopPropagation();
              if (selSym.kwhTarget) onHideKwh(selSym.kwhTarget);
              else onDeleteConsumer(selSym.d, selSym.c, selSym.i);
              setSelSym(null);
            }}
              onMouseDown={(e) => e.stopPropagation()} style={{ cursor: 'pointer' }} data-testid="delete-symbol-btn">
              <circle cx={selSym.x + 24} cy={selSym.y - 24} r={9} fill="#dc2626" />
              <line x1={selSym.x + 20.5} y1={selSym.y - 27.5} x2={selSym.x + 27.5} y2={selSym.y - 20.5} stroke="#fff" strokeWidth={1.8} />
              <line x1={selSym.x + 27.5} y1={selSym.y - 27.5} x2={selSym.x + 20.5} y2={selSym.y - 20.5} stroke="#fff" strokeWidth={1.8} />
            </g>
          </g>
        )}
      </g>
      {/* Handtekening elektricien (vaste strook boven het onderschrift, overlapt nooit met het schema) */}
      {hasSig && (
        <g data-testid="eendraad-signature">
          <rect x={totalW - 252} y={totalH - 158} width={244} height={98} fill="#fff" stroke="#555" strokeWidth={0.9} rx={3} />
          <text x={totalW - 245} y={totalH - 144} fontSize={9} fontWeight="bold" fill="#111">Handtekening installateur</text>
          <image href={signature.image} x={totalW - 245} y={totalH - 139} width={138} height={60} preserveAspectRatio="xMinYMid meet" />
          <text x={totalW - 245} y={totalH - 70} fontSize={9.5} fill="#111">{signature.name || ''}</text>
          <text x={totalW - 95} y={totalH - 70} fontSize={9.5} fill="#333" textAnchor="start">{signature.date ? new Date(signature.date).toLocaleDateString('nl-BE') : ''}</text>
        </g>
      )}
      {/* Vast onderschrift (buiten pan): locatie, installateur, type net */}
      <g data-testid="eendraad-footer">
        <line x1={10} y1={totalH - 44} x2={totalW - 10} y2={totalH - 44} stroke="#999" strokeWidth={0.8} />
        <text x={14} y={totalH - 31} fontSize={9} fontWeight="bold" fill="#111">
          {`Locatie elektrische installatie: ${meta?.customerName || '-'}${meta?.address ? ` \u2014 ${meta.address}` : ''}`}
        </text>
        <text x={14} y={totalH - 19} fontSize={8.5} fill="#333">
          {`Installateur: ${meta?.company?.name || ''}${meta?.company?.address ? ` \u00b7 ${meta.company.address}` : ''}${meta?.company?.phone ? ` \u00b7 ${meta.company.phone}` : ''}${meta?.company?.email ? ` \u00b7 ${meta.company.email}` : ''}${meta?.company?.vat ? ` \u00b7 BTW ${meta.company.vat}` : ''}`}
        </text>
        <text x={14} y={totalH - 7} fontSize={8.5} fill="#333">
          {`Type net: ${tree?.supply?.voltage || '230V'} ~ 50Hz \u00b7 ${tree?.supply?.amperage || 40}A \u00b7 Datum: ${new Date().toLocaleDateString('nl-BE')}`}
        </text>
      </g>
    </svg>
  );
});

export default EendraadCanvas;
